import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { BrandingProvider } from './context/BrandingContext';
import { registerServiceWorker } from './utils/pushNotifications';
import { LoginPage, RegisterPage } from './pages/AuthPages';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TodayPage } from './pages/TodayPage';
import { EnquiryWizard } from './pages/EnquiryWizard';
import { DecisionOutcomePage } from './pages/DecisionOutcomePage';
import { EnquiriesListPage } from './pages/EnquiriesListPage';
import { EnquiryDetailPage } from './pages/EnquiryDetailPage';
import { ArchivedEnquiriesPage } from './pages/ArchivedEnquiriesPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { PipelineFullPage } from './pages/PipelineFullPage';
import { ContactsPage } from './pages/ContactsPage';
import { QuotePage } from './pages/QuotePage';
import { ApprovalInboxPage } from './pages/ApprovalInboxPage';
import { DashboardFullPage } from './pages/DashboardFullPage';
import { LibraryPage } from './pages/LibraryPage';
import { AdminPage } from './pages/AdminPage';
import { ReportsPage } from './pages/ReportsPage';
import { CalendarPage } from './pages/CalendarPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import SequencesPage from './pages/SequencesPage';
import MyProfilePage from './pages/MyProfilePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HelpPage from './pages/HelpPage';
import TermsPage from './pages/TermsPage';
import { Toaster } from './components/ui/sonner';
import { InstallPrompt } from './components/InstallPrompt';
import './App.css';

// Build version for cache debugging
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || 'dev';
console.log(`[OpsFlow] Build version: ${BUILD_VERSION}`);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      <Route 
        path="/register" 
        element={user ? <Navigate to="/" replace /> : <RegisterPage />} 
      />
      <Route 
        path="/accept-invite" 
        element={user ? <Navigate to="/" replace /> : <AcceptInvitePage />} 
      />
      <Route 
        path="/privacy-policy" 
        element={<PrivacyPolicyPage />} 
      />
      <Route 
        path="/terms" 
        element={<TermsPage />} 
      />
      <Route 
        path="/verify-email" 
        element={<VerifyEmailPage />} 
      />
      <Route 
        path="/forgot-password" 
        element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} 
      />
      <Route 
        path="/reset-password" 
        element={user ? <Navigate to="/" replace /> : <ResetPasswordPage />} 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <TodayPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <MyProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardFullPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiries" 
        element={
          <ProtectedRoute>
            <EnquiriesListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiries/archived" 
        element={
          <ProtectedRoute>
            <ArchivedEnquiriesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/audit-logs" 
        element={
          <ProtectedRoute>
            <AuditLogsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiry/new" 
        element={
          <ProtectedRoute>
            <EnquiryWizard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiry/:enquiryId" 
        element={
          <ProtectedRoute>
            <EnquiryDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiry/:enquiryId/decision" 
        element={
          <ProtectedRoute>
            <DecisionOutcomePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/enquiry/:enquiryId/quote" 
        element={
          <ProtectedRoute>
            <QuotePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/quotes/:quoteId" 
        element={
          <ProtectedRoute>
            <QuotePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pipeline" 
        element={
          <ProtectedRoute>
            <PipelineFullPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contacts" 
        element={
          <ProtectedRoute>
            <ContactsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/library" 
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/approvals" 
        element={
          <ProtectedRoute>
            <ApprovalInboxPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/sequences" 
        element={
          <ProtectedRoute>
            <SequencesPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  // Register service worker at app root so push subscription checks work on any page
  useEffect(() => {
    registerServiceWorker();
    
    // Unlock screen orientation for PWA (if locked by old manifest)
    try {
      if (screen.orientation && screen.orientation.unlock) {
        screen.orientation.unlock();
      }
    } catch (_) { /* Not supported or not allowed — ignore */ }
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <WebSocketProvider>
          <BrandingProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
              <InstallPrompt />
            </BrowserRouter>
          </BrandingProvider>
        </WebSocketProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
